import type { IBotNetworkInterface } from '../types/bot_interface';
import type { IBotNetwork, IToBotMessagePacket, IFeedback, t_network_types, t_socket_state } from '../types/network';

import f1 from '@eab-pip/pi-f1';

import { AuthorizationServant } from '../servants/authorization_servant';
import { MessagingFacade } from '../facades/messaging_facade';
/**
 * support a simple web socket connection
 */
export class NetworkWS implements IBotNetwork {
  /**
   * the socket connection
   */
  sock: WebSocket | null;

  /**
   * if this is a new message then we need to start a new message, otherwise we amend to the existing message
   */
  private isNewMessage = true;

  /**
   * the timeout for reconnecting to ws server
   */
  private __reconnectTimeout: number;
  /**
   * the maximum number of attempts to reconnect to ws server
   */
  private __maxAttempts = 3;
  /**
   * number of attempts made to reconnect to ws server
   */
  private __currentAttempts = 0;

  /**
   * if the network already in streaming of a message, we need to disable the send button
   */
  __inStreaming = false;

  constructor(public stateInterface: IBotNetworkInterface, public messagingFacade: MessagingFacade) {}

  /**
   * if the uri begins with `ws` then it is a web socket connection
   */
  static isOf(uri: string): boolean {
    return uri.match(/^ws/) != null;
  }

  public get inStreaming(): boolean {
    return this.__inStreaming && ['open'].includes(this.getState());
  }

  /**
   * begin listening
   */
  async startup() {
    if (['connecting', 'open'].includes(this.getState())) {
      return Promise.resolve(this);
    }

    return this.listen();
  }

  /**
   * stop listening
   */
  async shutdown() {
    this.unlisten();
    return this;
  }

  /**
   * Connection state
   * @returns 'open' | 'closed' | 'connecting'
   */
  getState(): t_socket_state {
    const sockState = f1.to.string(f1.read('/readyState', '', this.sock));
    const states: Record<string, t_socket_state> = {
      '0': 'connecting',
      '1': 'open',
      '2': 'closing',
      '3': 'closed'
    };

    return f1.read(`/${sockState}`, sockState as t_socket_state, states);
  }

  /**
   * start the socket and wait for it to open and receive messages
   * @todo handle errors
   */
  async listen() {
    try {
      return this._listen();
    } catch (error) {
      this.__inStreaming = false;
      return Promise.reject(error);
    }
  }

  protected async _listen() {
    const uri = this.stateInterface.uriFn ? this.stateInterface.uriFn() : `${this.stateInterface.uri || ''}`;
    const newURI = await AuthorizationServant.computeURI(uri, this.stateInterface.ctmWs);

    const uriSettings = (this.stateInterface.uriSettings || []) as string[];
    const sock = (this.sock = new WebSocket(newURI, uriSettings));

    this._resetDelay();

    return new Promise<this>((resolve, reject) => {
      sock.onerror = (err) => {
        this.messagingFacade.logErrorMessage(`socket error: ${err}`);
        this.__inStreaming = false;
        reject(this);
      };

      sock.onopen = () => {
        resolve(this);

        sock.onmessage = (evt) => {
          const { text, more, token } = JSON.parse(evt.data) as { text: string; more: boolean; token: string };

          const botText = `${text || ''}`;
          const isNewMessage = this.isNewMessage;
          const hasMoreResponses = f1.is.true(more);
          this.__inStreaming = f1.is.true(more);

          if (isNewMessage) {
            this.messagingFacade.writeMessageFromBot(botText);
          } else if (hasMoreResponses) {
            this.messagingFacade.amendMessageFromBot(botText, 'immediate');
          }

          if (!hasMoreResponses) {
            this.messagingFacade.amendMessageFromBot('', 'immediate');
            this.messagingFacade.notifyStreamingDone(token);
          }

          // a new message begins when more is true
          //
          this.isNewMessage = !hasMoreResponses;
        };
      };

      sock.onclose = (event) => {
        if (!event.wasClean) {
          this.__handleError();
        }
      };
    });
  }

  private __handleError() {
    if (this.__currentAttempts < this.__maxAttempts) {
      this.__currentAttempts += 1;
      this.__reconnectTimeout = setTimeout(() => {
        this.listen();
      }, 1000);
    } else {
      this.messagingFacade.writeError(this.messagingFacade.errors.CONN_FAIL());
      this._resetAttempts();
      this._resetDelay();
    }
  }

  /**
   * close the socket
   */
  unlisten() {
    this._resetSocket();
    return Promise.resolve();
  }

  /**
   * if the socket is not open, then wait and attempt to send again
   * if the socket is open, then send the message as JSON
   *
   * @note requires `listen` to run successfully so there is a socket
   */
  send(packet: IToBotMessagePacket, attempt = 0) {
    if (attempt > 5) {
      this.messagingFacade.writeError('Something went wrong. Please try again.');
      return Promise.reject();
    }

    this.__inStreaming = true;
    this.__recoverFromNoResponse(packet);

    return new Promise<IToBotMessagePacket>((resolve, reject) => {
      if (!this.sock) {
        this.messagingFacade.logErrorMessage('Check the uri and uriFn provided is valid');
        return reject(`no socket connection`);
      }
      if (this.sock.CONNECTING === this.sock.readyState) {
        return new Promise<void>((resolve, reject) => {
          setTimeout(() => {
            this.send(packet, attempt + 1)
              .then(resolve)
              .catch(reject);
          }, 500);
        });
      }
      if (([this.sock.CLOSING, this.sock.CLOSED] as number[]).includes(this.sock.readyState)) {
        this.messagingFacade.writeError('Something went wrong. Please try again.');
        return reject(`socket is not open`);
      }

      const data = packet.feedback ?? packet.message;
      const json = JSON.stringify(data);

      this.sock.send(json);
      return resolve(packet);
    }).catch((err) => {
      this.__inStreaming = false;
      return err;
    });
  }

  /**
   * private messages may not have a response, so we should timeout
   */
  private __recoverFromNoResponse(packet: IToBotMessagePacket) {
    const timeout =
      packet.permission === 'private'
        ? setTimeout(() => {
            this.__inStreaming = false;
            clearTimeout(timeout);
          }, 1000)
        : setTimeout(f1.noop, 0);
  }

  /**
   * reset the socket and attempt state
   */
  resetConnection() {
    this._resetAttempts();
    this._resetSocket();
    this._resetDelay();
  }

  /**
   * clear the timer
   */
  protected _resetDelay() {
    clearTimeout(this.__reconnectTimeout);
  }

  /**
   * clear state associated with attempting to reconnect
   */
  protected _resetAttempts() {
    this.__currentAttempts = 0;
  }

  /**
   * clear state associated with the open socket
   */
  protected _resetSocket() {
    try {
      if (this.sock && this.sock.readyState !== WebSocket.CLOSED) {
        this.sock.close();
      }
    } catch {
      // we do not care if the close fails, we are dropping the connection
    }

    // always reset values regardless of previous state of socket
    this.sock = null;
    this.isNewMessage = true;
    this.__inStreaming = false;
  }

  /**
   * send a feedback to the bot for network connection
   */
  sendFeedback(feedback: IFeedback) {
    if (this.sock) {
      this.sock.send(JSON.stringify(feedback));
    }

    return Promise.resolve({});
  }

  /**
   * To know if the network connection is of ws
   */
  ofType(type: t_network_types): type is 'ws' {
    return type === 'ws';
  }
}
